import { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase/config';
import * as Icons from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import toast from 'react-hot-toast';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Quill szerkesztő eszköztár beállítások
const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
    ['link', 'image'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'align': [] }],
    [{ 'indent': '-1'}, { 'indent': '+1' }],
    [{ 'size': ['small', false, 'large', 'huge'] }],
    ['blockquote', 'code-block'],
    ['clean']
  ],
};

const quillFormats = [
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet',
  'header',
  'link', 'image',
  'color', 'background',
  'align', 'indent', 'size',
  'blockquote', 'code-block'
];

// CSS stílusok a leírás tartalmának megfelelő megjelenítéséhez
const descriptionStyles = `
  .description-content {
    max-height: 120px;
    overflow-y: auto;
  }
  .description-content h1, .description-content h2, .description-content h3 {
    margin-top: 0.5em;
    margin-bottom: 0.5em;
    font-weight: 600;
  }
  .description-content h1 {
    font-size: 1.25rem;
  }
  .description-content h2 {
    font-size: 1.15rem;
  }
  .description-content h3 {
    font-size: 1.05rem;
  }
  .description-content p {
    margin-bottom: 0.5em;
  }
  .description-content ul, .description-content ol {
    margin-left: 1.5em;
    margin-bottom: 0.5em;
  }
  .description-content li {
    margin-bottom: 0.25em;
  }
  .description-content a {
    color: #4f46e5;
    text-decoration: underline;
  }
  .description-content img {
    max-width: 100%;
    height: auto;
  }
`;

export default function DiscountManager() {
  const { t } = useTranslation();
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    percentage: 0,
    logoUrl: '',
    isActive: true,
    link: '',
    categoryId: '',
    couponCode: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [editorLoaded, setEditorLoaded] = useState(false);

  // Quill szerkesztő betöltésének kezelése
  useEffect(() => {
    setEditorLoaded(true);
  }, []);

  // Külön függvénybe szervezzük a kedvezmények lekérését
  const fetchDiscounts = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'discounts'));
      const discountsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setDiscounts(discountsData);
    } catch (error) {
      console.error('Error fetching discounts:', error);
      toast.error('Hiba történt a kedvezmények betöltésekor');
    } finally {
      setLoading(false);
    }
  };

  // Kategóriák lekérése
  const fetchCategories = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'discountCategories'));
      const categoriesData = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(cat => cat.isActive); // csak az aktív kategóriákat jelenítjük meg
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Hiba történt a kategóriák betöltésekor');
    }
  };

  useEffect(() => {
    fetchDiscounts();
    fetchCategories();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let logoUrl = formData.logoUrl;

      if (logoFile) {
        const storageRef = ref(storage, `discounts/${logoFile.name}`);
        await uploadBytes(storageRef, logoFile);
        logoUrl = await getDownloadURL(storageRef);
      }

      const discountData = {
        ...formData,
        logoUrl,
        updatedAt: new Date(),
      };

      if (selectedDiscount) {
        await updateDoc(doc(db, 'discounts', selectedDiscount.id), discountData);
        toast.success('Kedvezmény sikeresen módosítva');
      } else {
        await addDoc(collection(db, 'discounts'), {
          ...discountData,
          createdAt: new Date()
        });
        toast.success('Kedvezmény sikeresen létrehozva');
      }

      setIsModalOpen(false);
      setSelectedDiscount(null);
      setFormData({
        title: '',
        description: '',
        percentage: 0,
        logoUrl: '',
        isActive: true,
        link: '',
        categoryId: '',
        couponCode: ''
      });
      setLogoFile(null);
      
      // Lista frissítése
      await fetchDiscounts();
      
    } catch (error) {
      console.error('Error saving discount:', error);
      toast.error('Hiba történt a mentés során');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (discountId) => {
    if (window.confirm('Biztosan törli ezt a kedvezményt?')) {
      try {
        setLoading(true);
        await deleteDoc(doc(db, 'discounts', discountId));
        toast.success('Kedvezmény törölve');
        // Lista frissítése
        await fetchDiscounts();
      } catch (error) {
        console.error('Error deleting discount:', error);
        toast.error('Hiba történt a törlés során');
      } finally {
        setLoading(false);
      }
    }
  };

  const DynamicIcon = ({ iconName, ...props }) => {
    const Icon = Icons[iconName];
    return Icon ? <Icon {...props} /> : null;
  };

  const filteredDiscounts = selectedCategory === 'all' 
    ? discounts 
    : discounts.filter(d => d.categoryId === selectedCategory);

  return (
    <div className="p-6">
      <style>{descriptionStyles}</style>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Kedvezmények kezelése</h1>
          <p className="mt-1 text-sm text-gray-500">
            Kedvezmények létrehozása és kezelése
          </p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
        >
          <Icons.Plus className="h-4 w-4 mr-2" />
          Új kedvezmény
        </button>
      </div>

      {/* Category Filter */}
      <div className="mb-8">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`relative flex flex-col items-center justify-center p-4 rounded-xl transition-all transform hover:scale-105 ${
              selectedCategory === 'all'
                ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg'
                : 'bg-white text-gray-600 hover:bg-indigo-50 hover:shadow-md'
            } border border-gray-200`}
          >
            <Icons.LayoutGrid className={`h-6 w-6 mb-2 ${
              selectedCategory === 'all' ? 'text-white' : 'text-indigo-500'
            }`} />
            <span className="font-medium text-sm">Összes</span>
            <span className={`absolute top-2 right-2 ${
              selectedCategory === 'all'
                ? 'bg-white text-indigo-600'
                : 'bg-indigo-100 text-indigo-600'
            } px-2 py-0.5 rounded-full text-xs font-bold`}>
              {discounts.length}
            </span>
          </button>
          
          {categories.map(category => {
            const count = discounts.filter(d => d.categoryId === category.id).length;
            return (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`relative flex flex-col items-center justify-center p-4 rounded-xl transition-all transform hover:scale-105 ${
                  selectedCategory === category.id
                    ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-indigo-50 hover:shadow-md'
                } border border-gray-200`}
              >
                <DynamicIcon 
                  iconName={category.icon} 
                  className={`h-6 w-6 mb-2 ${
                    selectedCategory === category.id ? 'text-white' : 'text-indigo-500'
                  }`}
                />
                <span className="font-medium text-sm text-center">{category.name}</span>
                {count > 0 && (
                  <span className={`absolute top-2 right-2 ${
                    selectedCategory === category.id
                      ? 'bg-white text-indigo-600'
                      : 'bg-indigo-100 text-indigo-600'
                  } px-2 py-0.5 rounded-full text-xs font-bold`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Discounts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredDiscounts.map((discount) => (
          <div key={discount.id} className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-3">
                  {discount.logoUrl && (
                    <img 
                      src={discount.logoUrl} 
                      alt={discount.title}
                      className="h-10 w-10 object-contain rounded-full bg-gray-50"
                    />
                  )}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{discount.title}</h3>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-500">{discount.percentage}% kedvezmény</span>
                      {discount.categoryId && categories.find(c => c.id === discount.categoryId) && (
                        <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">
                          {categories.find(c => c.id === discount.categoryId).name}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      setSelectedDiscount(discount);
                      setFormData(discount);
                      setIsModalOpen(true);
                    }}
                    className="p-2 text-gray-400 hover:text-gray-600"
                  >
                    <Icons.Edit2 className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(discount.id)}
                    className="p-2 text-red-400 hover:text-red-600"
                  >
                    <Icons.Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <div className="text-sm text-gray-600 mb-4 description-content" dangerouslySetInnerHTML={{ __html: discount.description }} />
              <div className="flex items-center justify-between">
                <span className={`px-2 py-1 text-xs rounded-full ${
                  discount.isActive 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {discount.isActive ? 'Aktív' : 'Inaktív'}
                </span>
                <span className="text-sm text-gray-500">
                  Létrehozva: {discount.createdAt?.toDate().toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-6xl w-full p-10 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-8">
              <h2 className="text-2xl font-semibold text-gray-900">
                {selectedDiscount ? 'Kedvezmény szerkesztése' : 'Új kedvezmény'}
              </h2>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setSelectedDiscount(null);
                  setFormData({
                    title: '',
                    description: '',
                    percentage: 0,
                    logoUrl: '',
                    isActive: true,
                    link: '',
                    categoryId: '',
                    couponCode: ''
                  });
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <Icons.X className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Cím</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-4 py-3"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Kedvezmény mértéke (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.percentage}
                    onChange={(e) => setFormData({...formData, percentage: Number(e.target.value)})}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-4 py-3"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Leírás</label>
                {editorLoaded && (
                  <div className="border border-gray-300 rounded-md overflow-hidden">
                    <ReactQuill
                      value={formData.description}
                      onChange={(value) => setFormData({...formData, description: value})}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder="Adja meg a kedvezmény részletes leírását..."
                      style={{ height: '400px' }}
                    />
                  </div>
                )}
                <p className="mt-2 text-sm text-gray-500">
                  Használja a formázási lehetőségeket a szebb megjelenítéshez. A szerkesztő eszköztárban több formázási lehetőség is elérhető.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Logo</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setLogoFile(e.target.files[0])}
                    className="block w-full text-sm text-gray-500
                             file:mr-4 file:py-2 file:px-4
                             file:rounded-full file:border-0
                             file:text-sm file:font-semibold
                             file:bg-indigo-50 file:text-indigo-700
                             hover:file:bg-indigo-100"
                  />
                  {formData.logoUrl && (
                    <div className="mt-2 flex items-center">
                      <img 
                        src={formData.logoUrl} 
                        alt="Logo előnézet" 
                        className="h-12 w-12 object-contain rounded-full bg-gray-50 mr-2" 
                      />
                      <span className="text-xs text-gray-500">Jelenlegi logo</span>
                    </div>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Kategória</label>
                  <select
                    value={formData.categoryId}
                    onChange={(e) => setFormData({...formData, categoryId: e.target.value})}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-4 py-3"
                  >
                    <option value="">Válassz kategóriát</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Link</label>
                  <div className="relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Icons.Link className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="url"
                      value={formData.link}
                      onChange={(e) => setFormData({...formData, link: e.target.value})}
                      className="block w-full pl-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-4 py-3"
                      placeholder="https://"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Kuponkód</label>
                  <div className="relative rounded-md shadow-sm">
                    <input
                      type="text"
                      value={formData.couponCode}
                      onChange={(e) => setFormData({...formData, couponCode: e.target.value})}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-4 py-3"
                      placeholder="pl. KOD2025"
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <Icons.Tag className="h-5 w-5 text-gray-400" />
                    </div>
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    Opcionális. Ha megadod, a felhasználók megtekinthetik a kuponkódot.
                  </p>
                </div>
              </div>
              
              <div className="flex items-center bg-gray-50 p-6 rounded-lg">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                  className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="ml-3 block text-base text-gray-900">
                  Aktív kedvezmény
                </label>
                <div className="ml-auto flex items-center">
                  <span className={`px-3 py-1.5 text-sm rounded-full ${
                    formData.isActive 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {formData.isActive ? 'Aktív' : 'Inaktív'}
                  </span>
                </div>
              </div>
              
              <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    setSelectedDiscount(null);
                    setFormData({
                      title: '',
                      description: '',
                      percentage: 0,
                      logoUrl: '',
                      isActive: true,
                      link: '',
                      categoryId: '',
                      couponCode: ''
                    });
                  }}
                  className="px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
                >
                  Mégse
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-3 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 disabled:opacity-50 flex items-center"
                >
                  {loading ? (
                    <>
                      <Icons.Loader2 className="h-5 w-5 animate-spin mr-2" />
                      <span>Feldolgozás...</span>
                    </>
                  ) : (
                    <>
                      <Icons.Save className="h-5 w-5 mr-2" />
                      <span>Mentés</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}